import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { TreatmentWarning } from 'it/primatoo/openapi/api/org/schemas/treatment/TreatmentWarning';
import { WraV0GlobalMeasureCheck } from '../models/wra-v0-global-measure-check';
import { WraV0GlobalMeasureCheckValue } from '../models/wra-v0-global-measure-check-value';
import { WRAValueAnswer } from '../../v0/models/wraValueAnswer';
import { IoniconsOutline, WProperties, WRiskAssessmentProperties } from '@pt/pt-models';
import { eventListeners } from '@popperjs/core';

@Component({
  selector: 'pt-wra-v0-global-measure-selector',
  template: `
    <ng-container
      *ngIf="widgetProperties?.riskAssessmentViewMode === WRiskAssessmentProperties.FILL_FROM_TRIGGER_AND_SELECT">
      <ng-container *ngIf="enableGlobalMeasSelector">
        <div class="pt-widget-ra-v0-global-measure-selector">
          <label>{{ "WIDGET.RA.GLOBAL_CHECK_MEASURE" | translate }}</label>

          <ng-select
            style="width: 200px; margin-left: 10px;"
            appendTo="body"
            class="global-measure-check-selector pt-select--custom"
            [items]="queryGlobalMeasureCheckDocs"
            [bindValue]="'docId'"
            [disabled]="!editable"
            [bindLabel]="'name'"
            [clearable]="true"
            (ngModelChange)="loadGlobalMeasureCheck.emit($event)"
            [(ngModel)]="selectedGlobalMeasure">
            <ng-template ng-label-tmp let-item="item" let-clear="clear">
              <div class="d-flex flex-row align-items-center justify-between gms-label">
                <pt-icon *ngIf="!item.leMatched"
                         [key]='IconWarning'
                         container='body'
                         ngbTooltip="{{'TOOLTIP.WIDGET_RISK_ASSESSMENT.LE_MATCHED' | translate}}"
                         class="text-warning le-matched">
                </pt-icon>
                <div container='body'
                     class="text-truncate"
                     ngbTooltip="{{item.name}}">
                  {{ item.name }}
                </div>

              </div>
            </ng-template>

            <ng-template ng-option-tmp let-item="item" let-index="index">
              <div class="d-flex flex-row align-items-center justify-between gms-label">
                <pt-icon *ngIf="!item.leMatched"
                         [key]='IconWarning'
                         container='body'
                         ngbTooltip="{{'TOOLTIP.WIDGET_RISK_ASSESSMENT.LE_MATCHED' | translate}}"
                         class="text-warning le-matched">
                </pt-icon>
                <div container='body'
                     class="text-truncate "
                     ngbTooltip="{{item.name}}">
                  {{ item.name }}
                </div>
              </div>
            </ng-template>
          </ng-select>
        </div>

        <div class="alert alert-warning alert-widget-custom"
             *ngIf="enableApply">
          {{ 'WIDGET.RA.GLOBAL_CHECK_MEASURE_WARNING' | translate }}
          <button type='submit'
                  [disabled]="!enableApply || !editable"
                  (click)="applyChangeGlobalMeasureChecked.emit(selectedGlobalMeasure)"
                  class='btn btn-primary btn-sm ms-2'>
            {{ 'BUTTON.APPLY' | translate }}
          </button>
        </div>
      </ng-container>

      <div class="alert alert-warning alert-widget-custom" *ngIf="GLOBAL_MEAS_ANSWER_CHANGED">
        <div class="d-flex w-100">{{ 'MESSAGE.GLOBAL_MEAS_ANSWER_CHANGED' | translate }}</div>
        <button type='submit'
                [disabled]="!editable"
                (click)="alignGlobalMeasAnswerChanged.emit(false)"
                class='btn btn-primary btn-sm ms-2'>{{ 'BUTTON.ALIGN' | translate }}
        </button>
        <button type='submit'
                [disabled]="!editable"
                (click)="alignGlobalMeasAnswerChanged.emit(true)"
                class='btn btn-outline-secondary btn-sm ms-2'>{{ 'BUTTON.IGNORE' | translate }}
        </button>
      </div>

      <div class="alert alert-warning alert-widget-custom" *ngIf="GLOBAL_MEAS_LIST_CHANGED">
        <div class="d-flex w-100">{{ 'MESSAGE.GLOBAL_MEAS_LIST_CHANGED' | translate }}</div>
        <button type='submit'
                [disabled]="!editable"
                (click)="alignGlobalMeasListChanged.emit(false)"
                class='btn btn-primary btn-sm ms-2'>{{ 'BUTTON.ALIGN' | translate }}
        </button>

        <button type='submit'
                [disabled]="!editable"
                (click)="alignGlobalMeasListChanged.emit(true)"
                class='btn btn-outline-secondary btn-sm ms-2'>{{ 'BUTTON.IGNORE' | translate }}
        </button>
      </div>

    </ng-container>



  `,
  styles: [`
    .global-measure-check-selector .ng-value {
      z-index: 1000;
    }

    .gms-label  {
      z-index: 1000;
    }
    .pt-widget-ra-v0-global-measure-selector {
      display: flex;
      flex-direction: row;
      align-items: center;
      margin-bottom: 10px;
    }

    .alert-widget-custom {
      padding: 2px 10px;
      display: flex;
      align-items: center;
    }
    .le-matched {
      margin-right: 5px;
      display: flex;
    }
  `],
  encapsulation: ViewEncapsulation.None
})
export class WraV0GlobalMeasureSelectorComponent implements OnChanges {
  @Input() selectedGlobalMeasure?: number;
  @Input() warnings: TreatmentWarning[];
  @Input() answers: WRAValueAnswer[] = [];

  @Input() editable: boolean;
  @Input() widgetProperties: WProperties = {riskAssessmentViewMode: WRiskAssessmentProperties.FILL_FROM_QUERY};
  @Input() queryGlobalMeasureCheckDocs: WraV0GlobalMeasureCheck[] = [];
  @Input() queryGlobalMeasureCheck: WraV0GlobalMeasureCheckValue[] = [];

  @Output() loadGlobalMeasureCheck = new EventEmitter<number>();

  @Output() applyChangeGlobalMeasureChecked = new EventEmitter<number>();
  @Output() alignGlobalMeasListChanged = new EventEmitter<boolean>();
  @Output() alignGlobalMeasAnswerChanged = new EventEmitter<boolean>();
  WRiskAssessmentProperties = WRiskAssessmentProperties;
  public IconWarning = IoniconsOutline.warning;

  public currentSelectedGlobalMeasure?: number;
  public enableApply = false;
  public enableGlobalMeasSelector = false;

  public GLOBAL_MEAS_ANSWER_CHANGED = false;
  public GLOBAL_MEAS_LIST_CHANGED = false;

  ngOnChanges(changes: SimpleChanges): void {

    if (changes.selectedGlobalMeasure) {
      this.currentSelectedGlobalMeasure = changes.selectedGlobalMeasure.currentValue;
    }

    this.alignStatus();
  }

  private alignStatus() {
    this.GLOBAL_MEAS_ANSWER_CHANGED = false;
    this.GLOBAL_MEAS_LIST_CHANGED = false;

    switch (true) {
      case this.warnings.includes(TreatmentWarning.GLOBAL_MEAS_ANSWER_CHANGED): // Le valutazioni delle misure globali in uso sono state modificate.

        this.GLOBAL_MEAS_ANSWER_CHANGED = true;
        this.enableApply = false;
        this.enableGlobalMeasSelector = false;
        break;

      case this.warnings.includes(TreatmentWarning.GLOBAL_MEAS_LIST_CHANGED):   // La lista delle misure globali del registro è stata modificata.

        this.GLOBAL_MEAS_LIST_CHANGED = true;
        this.enableApply = false;
        this.enableGlobalMeasSelector = false;
        break;

      case this.currentSelectedGlobalMeasure !== this.selectedGlobalMeasure && !!this.selectedGlobalMeasure:
        this.enableApply = true;
        this.enableGlobalMeasSelector = true;
        break;

      case this.currentSelectedGlobalMeasure !== this.selectedGlobalMeasure && !!this.currentSelectedGlobalMeasure && this.selectedGlobalMeasure === null:
        this.enableApply = true;
        this.enableGlobalMeasSelector = true;
        break;

      default:
        this.enableApply = false;
        this.enableGlobalMeasSelector = this.widgetProperties?.riskAssessmentViewMode === WRiskAssessmentProperties.FILL_FROM_TRIGGER_AND_SELECT;

    }

    if (this.answers.length === 0) {
      // NO DATA
      this.enableApply = false;
      this.enableGlobalMeasSelector = false;
    }
  }

  cancel() {
    this.selectedGlobalMeasure = undefined;
    this.loadGlobalMeasureCheck.emit(this.selectedGlobalMeasure);
  }

  protected readonly eventListeners = eventListeners;
}


